import { memo, useContext, useMemo, type FunctionComponent } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useParams, Navigate } from 'react-router-dom';
// Skillmore UI Components
import useCurrentPath from '@empathco/ui-components/src/hooks/useCurrentPath';
import ContentCard from '@empathco/ui-components/src/elements/ContentCard';
import CardTabbar from '@empathco/ui-components/src/elements/CardTabbar';
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import DesktopOnly from '@empathco/ui-components/src/elements/DesktopOnly';
// local imports
import { getActingLeader, isManager } from '../models/user';
import useCustomerSettings from '../config/customer';
import usePathConfig, { PATH_HR_TEAMS, PATH_HR_TEAM_BUILDER, PATH_HR_TEAM_BUILDER_REVIEW } from '../config/paths';
import { getTeamId } from '../helpers/routerParams';
import useRedirects from '../helpers/redirect';
import { SupervisorContext } from '../context/supervisor';
import useVerifyUser from '../hooks/useVerifyUser';
import LeaderHeader from '../elements/LeaderHeader';
import Screen from '../v3/Screen';
import TeamView from '../components/TeamView';
import TeamBuilder from '../panels/TeamBuilder';
import TeamBuilderTeam from '../panels/TeamBuilderTeam';
import TeamBuilderReview from '../panels/TeamBuilderReview';

const HRTeamsScreen: FunctionComponent = () => {
  const { HAS_TEAM_BUILDER } = useCustomerSettings();
  const { ROUTES } = usePathConfig();
  const { getDefaultPath } = useRedirects();

  const team_id = getTeamId(useParams());
  const path = useCurrentPath(ROUTES);

  const { content, user } = useVerifyUser({ hr: true });
  const {
    tbTeam: { data: team },
    tbTeams: { acting_owner: teamsActingOwner }
  } = useContext(SupervisorContext);
  const isSupervisor = isManager(user);

  const leader = useMemo(
    () => getActingLeader(teamsActingOwner || team?.acting_owner),
    [teamsActingOwner, team]
  );

  const title = team_id === team?.id ? team.title : undefined;

  const breadcrumbs = useMemo(() =>
    (title && [{ title }]) ||
    (path === PATH_HR_TEAMS && [
      {
        title: HAS_TEAM_BUILDER
          ? (isSupervisor && 'breadcrumbs.hr.teams') || 'breadcrumbs.hr.teambuilder'
          : 'breadcrumbs.my_team'
      }
    ]) ||
    undefined, [title, path, isSupervisor, HAS_TEAM_BUILDER]);

  const items = useMemo(() => HAS_TEAM_BUILDER && isSupervisor ? [
    {
      title: 'supervisor.my_team.title',
      link: PATH_HR_TEAMS
    },
    {
      title: 'hr.teambuilder',
      active: Boolean(team_id),
      link: PATH_HR_TEAM_BUILDER
    }
  ] : undefined, [team_id, isSupervisor, HAS_TEAM_BUILDER]);

  if (!content && !HAS_TEAM_BUILDER && user && !isSupervisor) return <Navigate replace to={getDefaultPath(user)}/>;

  // Only Team Builder is guarded
  const teamBuilder = content ? undefined : (
    <DesktopOnly key={isSupervisor ? 2 : undefined}>
      {(team_id && (
        path === PATH_HR_TEAM_BUILDER_REVIEW ? (
          <TeamBuilderReview teamId={team_id}/>
        ) : (
          <DndProvider backend={HTML5Backend} context={window}>
            <TeamBuilderTeam teamId={team_id}/>
          </DndProvider>
        )
      )) || <TeamBuilder/>}
    </DesktopOnly>
  );

  return content || (
    <Screen withSettings breadcrumbs={breadcrumbs}>
      {leader ? <LeaderHeader leader={leader}/> : undefined}
      <ContentCard withoutTopMargin={Boolean(leader)}>
        {items ? (
          <CardTabbar
              withoutScroll
              wideAction
              items={items}
          >
            {[
              <TeamView key={1}/>,
              teamBuilder
            ]}
          </CardTabbar>
        ) : (
          <>
            <CardTitle title={HAS_TEAM_BUILDER ? 'hr.teambuilder' : 'supervisor.my_team.title'} withDivider/>
            {HAS_TEAM_BUILDER ? teamBuilder : <TeamView/>}
          </>
        )}
      </ContentCard>
    </Screen>
  );
};

export default memo(HRTeamsScreen);
